import { AsyncPipe, NgIf, NgTemplateOutlet } from '@angular/common';
import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Language } from '@lx/language';
import { Course } from '@lx/shared/data-access/types';
import { DateFormat } from '@lx/shared/i18n';
import { DatePipe } from '@lx/shared/ui/pipes';
import { Observable, skip, switchMap } from 'rxjs';
import { CourseService, SchoolLocationService } from '../../services';
import { CourseState, CourseStore, SharedDataStore } from '../../store';
import { CourseDetails } from '@lx/quote-tool/lite/shared/types';
import { CoursePayload, FixedDate, GroupedSchoolLocation } from '../../types';
import { SchoolLocation, SelectableField } from '@lx/quote-tool/lite/shared/types';
import { FixedDateSelectorFieldComponent } from '../fixed-date-selector-field/fixed-date-selector-field.component';
import { JuniorsCourseSelectorFieldComponent } from '../juniors-course-selector-field/juniors-course-selector-field.component';
import { LocationSelectorFieldComponent } from '../location-selector-field/location-selector-field.component';
import { SingleSelectionButtonListFieldComponent } from '../single-selection-button-list-field/single-selection-button-list-field.component';
import { getCourseSelectionProviders } from '../../utils';

@Component({
  standalone: true,
  selector: 'lx-language-camps',
  templateUrl: './language-camps.component.html',
  styleUrls: ['./language-camps.component.scss'],
  imports: [
    SingleSelectionButtonListFieldComponent,
    LocationSelectorFieldComponent,
    AsyncPipe,
    FormsModule,
    NgIf,
    JuniorsCourseSelectorFieldComponent,
    FixedDateSelectorFieldComponent,
    DatePipe,
    NgTemplateOutlet,
  ],
  providers: [...getCourseSelectionProviders()],
})
export class LanguageCampsComponent implements OnInit {
  @Input() withDescription = true;
  @Output() coursePayload = new EventEmitter<CoursePayload>();
  @Output() validCoursePayload = new EventEmitter<boolean>();

  languages$: Observable<Language[]> | undefined;

  courseDetails$: Observable<Partial<CourseState>> | undefined;
  startDates: FixedDate[] = [];
  endDates: FixedDate[] = [];
  courses: Course[] | undefined;
  groupedSchoolLocations$: Observable<GroupedSchoolLocation[]> | undefined;

  readonly emptySelectionMsg = $localize`Select`;
  protected readonly DateFormat = DateFormat;

  private readonly courseStore = inject(CourseStore);
  private readonly sharedDataStore = inject(SharedDataStore);
  private readonly courseService = inject(CourseService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly schoolLocationService = inject(SchoolLocationService);

  constructor() {
    this.languages$ = this.sharedDataStore.languages$;
    this.courseDetails$ = this.courseStore.courseDetails$;
  }

  ngOnInit(): void {
    this.courseStore.selectedSchoolLocationAndLanguage$.pipe(skip(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.resetDates();
    });

    this.courseStore.selectedSchoolLocation$
      .pipe(
        switchMap((schoolLocation) => this.courseService.getJuniorCourses(schoolLocation?.schoolCode)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((courses) => (this.courses = courses));
    this.courseStore.isJuniorValid$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((valid) => this.validCoursePayload.emit(valid));
    this.courseDetails$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((courseDetails) => this.coursePayload.emit(courseDetails));
    this.courseStore.selectedCourseAndStartDate$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((state: CourseState) => {
      this.handleCourseDates(state);
    });

    this.groupedSchoolLocations$ = this.schoolLocationService.getGroupedSchoolLocations();

    this.courseStore.selectedCourseStartDateAndEndDate$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ selectedCourse, selectedStartDate, selectedEndDate }) => {
        const duration = selectedCourse?.courseDates?.find(({ from, until }) => from === selectedStartDate && until === selectedEndDate)?.length;
        this.updateValueInStore(duration, 'selectedDuration');
      });
  }

  updateSelectedLanguage(selectedLanguage: string) {
    this.updateValueInStore(selectedLanguage, 'selectedLanguage');
  }

  updateSelectedSchoolLocation(schoolLocation: SchoolLocation) {
    this.updateValueInStore(schoolLocation, 'selectedSchoolLocation');
  }

  updateSelectedCourse(selectedCourse: SelectableField<CourseDetails>) {
    this.updateValueInStore(selectedCourse.value, 'selectedCourse');
  }

  updateSelectedStartDate(selectedStartDate: string) {
    this.updateValueInStore(selectedStartDate, 'selectedStartDate');
    this.updateValueInStore(undefined, 'selectedEndDate');
  }

  updateSelectedEndDate(selectedEndDate: string) {
    this.updateValueInStore(selectedEndDate, 'selectedEndDate');
  }

  private updateValueInStore(value: string | SchoolLocation | CourseDetails | number | undefined, key: keyof CoursePayload) {
    this.courseStore.updateState({ [key]: value });
  }

  private handleCourseDates({ selectedCourse, selectedStartDate }: CourseState): void {
    const courseDates = selectedCourse?.courseDates;

    // Extract unique start dates only for confirmed dates
    this.startDates = [...new Set(courseDates?.filter(({ confirmed }) => confirmed).map(({ from }) => from))]?.map((date) => ({ date }));

    if (selectedStartDate && courseDates) {
      // Get both endDate and duration for confirmed dates
      this.endDates = courseDates
        .filter(({ from, confirmed }) => from === selectedStartDate && confirmed)
        .map(({ until, length }) => ({
          date: until,
          duration: length,
        }));
    }
  }

  private resetDates() {
    this.updateValueInStore(undefined, 'selectedStartDate');
    this.updateValueInStore(undefined, 'selectedEndDate');
  }
}
